import gsap from 'gsap';
import { useEffect, useRef } from 'react';

/**
 * 动画角色组件（移植自 katavii/animated-login 项目的 AnimatedCharacters）
 * 包含 4 只小怪物：紫色（高个）、黑色（中个）、橙色（矮胖）、黄色（圆头）
 * 交互效果：
 *  - 眼球跟随鼠标/手指移动
 *  - 随机眨眼
 *  - 输入账号时角色互相张望、身体倾斜
 *  - 输入密码（隐藏状态）时紫色角色歪身遮挡
 *  - 显示密码时所有角色扭头偷看，并伴随间歇性"偷瞄"动画
 */

// ─── 简单的瞳孔点 ───────────────────────────────────────────────────────────

function Pupil({ size = 12, maxDistance = 5, pupilColor = 'black' }) {
  return (
    <div
      data-max-distance={maxDistance}
      className="pupil"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: pupilColor,
        willChange: 'transform',
      }}
    />
  );
}

// ─── 带眼白的眼球 ───────────────────────────────────────────────────────────

function EyeBall({ size = 48, pupilSize = 16, maxDistance = 10, eyeColor = 'white', pupilColor = 'black' }) {
  return (
    <div
      className="eyeball"
      data-max-distance={maxDistance}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: eyeColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        willChange: 'height',
      }}
    >
      <div
        className="eyeball-pupil"
        style={{
          width: pupilSize,
          height: pupilSize,
          borderRadius: '50%',
          backgroundColor: pupilColor,
          willChange: 'transform',
        }}
      />
    </div>
  );
}

// ─── 动画角色主组件 ─────────────────────────────────────────────────────────

export function AnimatedCharacters({
  isTyping = false,
  showPassword = false,
  passwordLength = 0,
}) {
  const containerRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const rafIdRef = useRef(0);

  // 四个角色的身体 ref
  const purpleRef = useRef(null);
  const blackRef = useRef(null);
  const orangeRef = useRef(null);
  const yellowRef = useRef(null);

  // 四个角色的脸部 ref
  const purpleFaceRef = useRef(null);
  const blackFaceRef = useRef(null);
  const orangeFaceRef = useRef(null);
  const yellowFaceRef = useRef(null);

  // 黄色角色的嘴巴 ref
  const yellowMouthRef = useRef(null);

  const purpleBlinkTimerRef = useRef();
  const blackBlinkTimerRef = useRef();
  const purplePeekTimerRef = useRef();

  const isHidingPassword = passwordLength > 0 && !showPassword;
  const isShowingPassword = passwordLength > 0 && showPassword;

  const isLookingRef = useRef(false);
  const lookingTimerRef = useRef();

  // 用 ref 同步最新状态，供 rAF 循环读取，避免闭包读到旧值
  const stateRef = useRef({ isTyping, isHidingPassword, isShowingPassword, isLooking: false });
  stateRef.current = {
    isTyping,
    isHidingPassword,
    isShowingPassword,
    isLooking: isLookingRef.current,
  };

  // 挂载时重置瞳孔位置（对应参考项目 useGSAP 中的初始化）
  useEffect(() => {
    if (!containerRef.current) return;
    gsap.set(containerRef.current.querySelectorAll('.pupil'), { x: 0, y: 0 });
    gsap.set(containerRef.current.querySelectorAll('.eyeball-pupil'), { x: 0, y: 0 });
  }, []);

  // contextSafe 在此处仅作为直通包装，保持与参考项目一致的调用形式
  const contextSafe = (fn) => fn;

  // gsap.quickTo 缓存集合
  const quickToRef = useRef(null);

  useEffect(() => {
    if (
      !purpleRef.current ||
      !blackRef.current ||
      !orangeRef.current ||
      !yellowRef.current ||
      !purpleFaceRef.current ||
      !blackFaceRef.current ||
      !orangeFaceRef.current ||
      !yellowFaceRef.current ||
      !yellowMouthRef.current
    )
      return;

    const qt = {
      purpleSkew: gsap.quickTo(purpleRef.current, 'skewX', { duration: 0.3, ease: 'power2.out' }),
      blackSkew: gsap.quickTo(blackRef.current, 'skewX', { duration: 0.3, ease: 'power2.out' }),
      orangeSkew: gsap.quickTo(orangeRef.current, 'skewX', { duration: 0.3, ease: 'power2.out' }),
      yellowSkew: gsap.quickTo(yellowRef.current, 'skewX', { duration: 0.3, ease: 'power2.out' }),
      purpleX: gsap.quickTo(purpleRef.current, 'x', { duration: 0.3, ease: 'power2.out' }),
      blackX: gsap.quickTo(blackRef.current, 'x', { duration: 0.3, ease: 'power2.out' }),
      purpleHeight: gsap.quickTo(purpleRef.current, 'height', { duration: 0.3, ease: 'power2.out' }),
      purpleFaceLeft: gsap.quickTo(purpleFaceRef.current, 'left', { duration: 0.3, ease: 'power2.out' }),
      purpleFaceTop: gsap.quickTo(purpleFaceRef.current, 'top', { duration: 0.3, ease: 'power2.out' }),
      blackFaceLeft: gsap.quickTo(blackFaceRef.current, 'left', { duration: 0.3, ease: 'power2.out' }),
      blackFaceTop: gsap.quickTo(blackFaceRef.current, 'top', { duration: 0.3, ease: 'power2.out' }),
      orangeFaceX: gsap.quickTo(orangeFaceRef.current, 'x', { duration: 0.2, ease: 'power2.out' }),
      orangeFaceY: gsap.quickTo(orangeFaceRef.current, 'y', { duration: 0.2, ease: 'power2.out' }),
      yellowFaceX: gsap.quickTo(yellowFaceRef.current, 'x', { duration: 0.2, ease: 'power2.out' }),
      yellowFaceY: gsap.quickTo(yellowFaceRef.current, 'y', { duration: 0.2, ease: 'power2.out' }),
      mouthX: gsap.quickTo(yellowMouthRef.current, 'x', { duration: 0.2, ease: 'power2.out' }),
      mouthY: gsap.quickTo(yellowMouthRef.current, 'y', { duration: 0.2, ease: 'power2.out' }),
    };
    quickToRef.current = qt;

    // 计算角色身体/脸部朝向鼠标的目标位置
    const calcPos = (el) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 3;
      const dx = mouseRef.current.x - cx;
      const dy = mouseRef.current.y - cy;
      return {
        faceX: Math.max(-15, Math.min(15, dx / 20)),
        faceY: Math.max(-10, Math.min(10, dy / 30)),
        bodySkew: Math.max(-6, Math.min(6, -dx / 120)),
      };
    };

    // 计算瞳孔在最大距离内的偏移（朝鼠标方向）
    const calcEyePos = (el, maxDist) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = mouseRef.current.x - cx;
      const dy = mouseRef.current.y - cy;
      const dist = Math.min(Math.sqrt(dx ** 2 + dy ** 2), maxDist);
      const angle = Math.atan2(dy, dx);
      return { x: Math.cos(angle) * dist, y: Math.sin(angle) * dist };
    };

    // 每帧驱动所有角色的姿态
    const tick = () => {
      const container = containerRef.current;
      if (!container) return;

      const {
        isTyping: typing,
        isHidingPassword: hiding,
        isShowingPassword: showing,
        isLooking: looking,
      } = stateRef.current;

      if (purpleRef.current && !showing) {
        const pp = calcPos(purpleRef.current);
        if (typing || hiding) {
          // 打字/藏密码时：紫色角色歪身、右移、拉高遮挡视线
          qt.purpleSkew(pp.bodySkew - 12);
          qt.purpleX(40);
          qt.purpleHeight(440);
        } else {
          qt.purpleSkew(pp.bodySkew);
          qt.purpleX(0);
          qt.purpleHeight(400);
        }
      }

      if (blackRef.current && !showing) {
        const bp = calcPos(blackRef.current);
        if (looking) {
          qt.blackSkew(bp.bodySkew * 1.5 + 10);
          qt.blackX(20);
        } else if (typing || hiding) {
          qt.blackSkew(bp.bodySkew * 1.5);
          qt.blackX(0);
        } else {
          qt.blackSkew(bp.bodySkew);
          qt.blackX(0);
        }
      }

      if (orangeRef.current && !showing) {
        const op = calcPos(orangeRef.current);
        qt.orangeSkew(op.bodySkew);
      }

      if (yellowRef.current && !showing) {
        const yp = calcPos(yellowRef.current);
        qt.yellowSkew(yp.bodySkew);
      }

      if (purpleRef.current && !showing && !looking) {
        const pp = calcPos(purpleRef.current);
        const purpleFaceX = pp.faceX >= 0 ? Math.min(25, pp.faceX * 1.5) : pp.faceX;
        qt.purpleFaceLeft(45 + purpleFaceX);
        qt.purpleFaceTop(40 + pp.faceY);
      }

      if (blackRef.current && !showing && !looking) {
        const bp = calcPos(blackRef.current);
        qt.blackFaceLeft(26 + bp.faceX);
        qt.blackFaceTop(32 + bp.faceY);
      }

      if (orangeRef.current && !showing) {
        const op = calcPos(orangeRef.current);
        qt.orangeFaceX(op.faceX);
        qt.orangeFaceY(op.faceY);
      }

      if (yellowRef.current && !showing) {
        const yp = calcPos(yellowRef.current);
        qt.yellowFaceX(yp.faceX);
        qt.yellowFaceY(yp.faceY);
      }

      if (yellowRef.current && !showing) {
        const yp = calcPos(yellowRef.current);
        qt.mouthX(yp.faceX);
        qt.mouthY(yp.faceY);
      }

      if (!showing) {
        // 所有瞳孔跟随鼠标
        const allPupils = container.querySelectorAll('.pupil');
        allPupils.forEach((p) => {
          const el = p;
          const maxDist = Number(el.dataset.maxDistance) || 5;
          const ePos = calcEyePos(el, maxDist);
          gsap.set(el, { x: ePos.x, y: ePos.y });
        });

        if (!looking) {
          // 眼白内的瞳孔跟随鼠标
          const allEyeballs = container.querySelectorAll('.eyeball');
          allEyeballs.forEach((eb) => {
            const el = eb;
            const maxDist = Number(el.dataset.maxDistance) || 10;
            const pupil = el.querySelector('.eyeball-pupil');
            if (!pupil) return;
            const ePos = calcEyePos(el, maxDist);
            gsap.set(pupil, { x: ePos.x, y: ePos.y });
          });
        }
      }

      rafIdRef.current = requestAnimationFrame(tick);
    };

    // 鼠标/触摸移动统一处理（移动端无鼠标，用 touchmove 驱动视线）
    const onMove = (e) => {
      const p = e.touches && e.touches.length ? e.touches[0] : e;
      if (p && typeof p.clientX === 'number') {
        mouseRef.current = { x: p.clientX, y: p.clientY };
      }
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    rafIdRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
      cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  // 紫色角色随机眨眼
  useEffect(() => {
    const purpleEyeballs = purpleRef.current ? purpleRef.current.querySelectorAll('.eyeball') : null;
    if (!purpleEyeballs || !purpleEyeballs.length) return;

    const scheduleBlink = () => {
      purpleBlinkTimerRef.current = setTimeout(() => {
        purpleEyeballs.forEach((el) => {
          gsap.to(el, { height: 2, duration: 0.08, ease: 'power2.in' });
        });
        setTimeout(() => {
          purpleEyeballs.forEach((el) => {
            const size = Number(el.style.width.replace('px', '')) || 18;
            gsap.to(el, { height: size, duration: 0.08, ease: 'power2.out' });
          });
          scheduleBlink();
        }, 150);
      }, Math.random() * 4000 + 3000);
    };

    scheduleBlink();
    return () => clearTimeout(purpleBlinkTimerRef.current);
  }, []);

  // 黑色角色随机眨眼
  useEffect(() => {
    const blackEyeballs = blackRef.current ? blackRef.current.querySelectorAll('.eyeball') : null;
    if (!blackEyeballs || !blackEyeballs.length) return;

    const scheduleBlink = () => {
      blackBlinkTimerRef.current = setTimeout(() => {
        blackEyeballs.forEach((el) => {
          gsap.to(el, { height: 2, duration: 0.08, ease: 'power2.in' });
        });
        setTimeout(() => {
          blackEyeballs.forEach((el) => {
            const size = Number(el.style.width.replace('px', '')) || 16;
            gsap.to(el, { height: size, duration: 0.08, ease: 'power2.out' });
          });
          scheduleBlink();
        }, 150);
      }, Math.random() * 4000 + 3000);
    };

    scheduleBlink();
    return () => clearTimeout(blackBlinkTimerRef.current);
  }, []);

  // 输入账号时：两个高个角色互相张望
  const applyLookAtEachOther = contextSafe(() => {
    const qt = quickToRef.current;
    if (qt) {
      qt.purpleFaceLeft(55);
      qt.purpleFaceTop(65);
      qt.blackFaceLeft(32);
      qt.blackFaceTop(12);
    }
    if (purpleRef.current) {
      purpleRef.current.querySelectorAll('.eyeball-pupil').forEach((p) => {
        gsap.to(p, { x: 3, y: 4, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
      });
    }
    if (blackRef.current) {
      blackRef.current.querySelectorAll('.eyeball-pupil').forEach((p) => {
        gsap.to(p, { x: 0, y: -4, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
      });
    }
  });

  // 隐藏密码时：紫色角色歪身挡住视线
  const applyHidingPassword = contextSafe(() => {
    const qt = quickToRef.current;
    if (qt) {
      qt.purpleFaceLeft(55);
      qt.purpleFaceTop(65);
    }
  });

  // 显示密码时：所有角色扭头围观
  const applyShowPassword = contextSafe(() => {
    const qt = quickToRef.current;
    if (qt) {
      qt.purpleSkew(0);
      qt.blackSkew(0);
      qt.orangeSkew(0);
      qt.yellowSkew(0);
      qt.purpleX(0);
      qt.blackX(0);
      qt.purpleHeight(400);

      qt.purpleFaceLeft(20);
      qt.purpleFaceTop(35);
      qt.blackFaceLeft(10);
      qt.blackFaceTop(28);
      qt.orangeFaceX(50 - 82);
      qt.orangeFaceY(85 - 90);
      qt.yellowFaceX(20 - 52);
      qt.yellowFaceY(35 - 40);
      qt.mouthX(10 - 40);
      qt.mouthY(0);
    }

    if (purpleRef.current) {
      purpleRef.current.querySelectorAll('.eyeball-pupil').forEach((p) => {
        gsap.to(p, { x: -4, y: -4, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
      });
    }
    if (blackRef.current) {
      blackRef.current.querySelectorAll('.eyeball-pupil').forEach((p) => {
        gsap.to(p, { x: -4, y: -4, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
      });
    }
    if (orangeRef.current) {
      orangeRef.current.querySelectorAll('.pupil').forEach((p) => {
        gsap.to(p, { x: -5, y: -4, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
      });
    }
    if (yellowRef.current) {
      yellowRef.current.querySelectorAll('.pupil').forEach((p) => {
        gsap.to(p, { x: -5, y: -4, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
      });
    }
  });

  // 显示密码期间：紫色角色间歇性"偷瞄"
  useEffect(() => {
    if (!isShowingPassword || passwordLength <= 0) {
      clearTimeout(purplePeekTimerRef.current);
      return;
    }

    const purpleEyePupils = purpleRef.current ? purpleRef.current.querySelectorAll('.eyeball-pupil') : null;
    if (!purpleEyePupils || !purpleEyePupils.length) return;

    const schedulePeek = () => {
      purplePeekTimerRef.current = setTimeout(() => {
        purpleEyePupils.forEach((p) => {
          gsap.to(p, { x: 4, y: 5, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
        });
        const qt = quickToRef.current;
        if (qt) {
          qt.purpleFaceLeft(20);
          qt.purpleFaceTop(35);
        }

        setTimeout(() => {
          purpleEyePupils.forEach((p) => {
            gsap.to(p, { x: -4, y: -4, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
          });
          schedulePeek();
        }, 800);
      }, Math.random() * 3000 + 2000);
    };

    schedulePeek();
    return () => clearTimeout(purplePeekTimerRef.current);
  }, [isShowingPassword, passwordLength]);

  // 输入账号时触发"互相张望"效果，800ms 后恢复
  useEffect(() => {
    if (isTyping && !isShowingPassword) {
      isLookingRef.current = true;
      stateRef.current.isLooking = true;
      applyLookAtEachOther();

      clearTimeout(lookingTimerRef.current);
      lookingTimerRef.current = setTimeout(() => {
        isLookingRef.current = false;
        stateRef.current.isLooking = false;
        if (purpleRef.current) {
          purpleRef.current.querySelectorAll('.eyeball-pupil').forEach((p) => {
            gsap.killTweensOf(p);
          });
        }
      }, 800);
    } else {
      clearTimeout(lookingTimerRef.current);
      isLookingRef.current = false;
      stateRef.current.isLooking = false;
    }
    return () => clearTimeout(lookingTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTyping, isShowingPassword]);

  // 密码显示/隐藏切换时应用对应姿态
  useEffect(() => {
    if (isShowingPassword) {
      applyShowPassword();
    } else if (isHidingPassword) {
      applyHidingPassword();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHidingPassword, isShowingPassword]);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: 550, height: 400 }}>
      {/* 紫色高个角色（顶部定位：打字/遮密码拉高时向下延伸，避免头部与眼睛被顶部裁切） */}
      <div
        ref={purpleRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 70,
          width: 180,
          height: 400,
          backgroundColor: '#6C3FF5',
          borderRadius: '10px 10px 0 0',
          zIndex: 1,
          transformOrigin: 'bottom center',
          willChange: 'transform',
        }}
      >
        <div
          ref={purpleFaceRef}
          style={{
            position: 'absolute',
            display: 'flex',
            gap: 32,
            left: 45,
            top: 40,
          }}
        >
          <EyeBall size={18} pupilSize={7} maxDistance={5} eyeColor="white" pupilColor="#2D2D2D" />
          <EyeBall size={18} pupilSize={7} maxDistance={5} eyeColor="white" pupilColor="#2D2D2D" />
        </div>
      </div>

      {/* 黑色中个角色 */}
      <div
        ref={blackRef}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 240,
          width: 120,
          height: 310,
          backgroundColor: '#2D2D2D',
          borderRadius: '8px 8px 0 0',
          zIndex: 2,
          transformOrigin: 'bottom center',
          willChange: 'transform',
        }}
      >
        <div
          ref={blackFaceRef}
          style={{
            position: 'absolute',
            display: 'flex',
            gap: 24,
            left: 26,
            top: 32,
          }}
        >
          <EyeBall size={16} pupilSize={6} maxDistance={4} eyeColor="white" pupilColor="#2D2D2D" />
          <EyeBall size={16} pupilSize={6} maxDistance={4} eyeColor="white" pupilColor="#2D2D2D" />
        </div>
      </div>

      {/* 橙色矮胖角色 */}
      <div
        ref={orangeRef}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: 240,
          height: 200,
          backgroundColor: '#FF9B6B',
          borderRadius: '120px 120px 0 0',
          zIndex: 3,
          transformOrigin: 'bottom center',
          willChange: 'transform',
        }}
      >
        <div
          ref={orangeFaceRef}
          style={{
            position: 'absolute',
            display: 'flex',
            gap: 32,
            left: 82,
            top: 90,
          }}
        >
          <Pupil size={12} maxDistance={5} pupilColor="#2D2D2D" />
          <Pupil size={12} maxDistance={5} pupilColor="#2D2D2D" />
        </div>
      </div>

      {/* 黄色圆头角色（带嘴巴） */}
      <div
        ref={yellowRef}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 310,
          width: 140,
          height: 230,
          backgroundColor: '#E8D754',
          borderRadius: '70px 70px 0 0',
          zIndex: 4,
          transformOrigin: 'bottom center',
          willChange: 'transform',
        }}
      >
        <div
          ref={yellowFaceRef}
          style={{
            position: 'absolute',
            display: 'flex',
            gap: 24,
            left: 52,
            top: 40,
          }}
        >
          <Pupil size={12} maxDistance={5} pupilColor="#2D2D2D" />
          <Pupil size={12} maxDistance={5} pupilColor="#2D2D2D" />
        </div>
        <div
          ref={yellowMouthRef}
          style={{
            position: 'absolute',
            width: 80,
            height: 4,
            backgroundColor: '#2D2D2D',
            borderRadius: 9999,
            left: 40,
            top: 88,
          }}
        />
      </div>
    </div>
  );
}

export default AnimatedCharacters;
